import React, { useEffect, useState, useRef } from 'react';
import Maintenance from './maintenance';
import PricingBreakdown from './pricingbreakdown';
import QuickAction from './quickaction';
import QuoteOrderBar from './quoteorderbar';
import TotalCost from './totalcost';
import TrustPilot from './trustpilot';
import Typography from '@components/typography';
import FinalisedOptions from '@components/configure/details/finalisedoptions';

import { canScroll } from '@store/reducers/layout';
import { setFinalisedState } from '@store/reducers/finalisedoptions';
import { updateVehiclePricingType } from '@store/reducers/vehicleinformation';

import { useAppDispatch, useAppSelector } from '@hooks/statehooks';
import { useDeviceDetect } from '@hooks/usedevicedetect';
import { usePricingBreakdown } from '@hooks/usepricingbreakdown';
import { usePricingOptions } from '@hooks/usepricingoptions';
import useModal from '@hooks/usemodal';

import '@styles/configure/sidebar.scss';
import { useSitcoreComponentData } from '@hooks/usesitecorecomponentdata';
import { VehicleConfiguration } from '@tsapiclient/zenauto.vehiclequotation.ts.client/interfaces/vehicleconfiguration';
import {
  howFarTooltip,
  howLongTooltip,
  howMuchInitialPaymentTooltip,
} from '../../../resources';
import useElementSize from '@hooks/useelementsize';
import Loader from '@components/loader/loader';
import { PricingType } from '../../../enums';
import { useLocation } from 'react-router-dom';
import { usePricing } from '@hooks/usepricing';
import { useVehicle } from '@hooks/usevehicle';
import {
  businessContractHire,
  personalContractHire,
  cookieName,
  siteWidePersonalBusinessToggleSelector,
  initialPaymentPriceExc,
  initialPaymentPriceInc,
} from '../../../constants';
import { cookieExists, createCookie } from '../../../utils/cookiehelper';

const Sidebar: React.FC = () => {
  const search = useLocation().search;
  const searchParamsFromUrl = new URLSearchParams(search);
  const expandConfig = searchParamsFromUrl.get('expandConfig');

  const { getVehicleInfo } = useVehicle();
  const [showCustomise, setShowCustomise] = useState<boolean>(
    expandConfig === 'true'
  );
  const [loadedTrustpilot, setLoadedTrustpilot] = useState<boolean>(false);
  const { isMobile, isTablet } = useDeviceDetect();
  const isMobileOrTablet = isTablet || isMobile;
  const pricingBreakdownItems = usePricingBreakdown();
  const { ready, pricing } = usePricingOptions();
  const { sitecoreReady, config } = useSitcoreComponentData();
  const modalSettings = useModal();
  const dispatch = useAppDispatch();

  const { initialPayment } = useAppSelector((state) => state.vehiclePrice);
  const pricingSelector = useAppSelector((state) => state.vehiclePrice.pricing);
  const pricingState = useAppSelector((state) => state.pricingState);
  const finalisedOptions = useAppSelector((state) => state.finalisedOptions);
  const configuration = useAppSelector((state) => state.vehicleConfiguration);
  const searchParams = useAppSelector((state) => state.vehicleSearchParams);
  const vehicleInformation = useAppSelector(
    (state) => state.vehicleInformation
  );

  const { updateAllPricing } = usePricing();

  const { colours, options } = configuration as VehicleConfiguration;

  const loadingFinalisedOptions =
    finalisedOptions.colours.paint.length === 0 &&
    finalisedOptions.colours.trim.length === 0 &&
    finalisedOptions.colours.hood.length === 0 &&
    finalisedOptions.options.length === 0;

  const leaseRef = useRef<HTMLDivElement>(null);
  const trustPiltoRef = useRef<HTMLDivElement>(null);

  const lease = useElementSize(leaseRef);
  const trustPilot = useElementSize(trustPiltoRef);

  const setSidebarCSSVariable = (variable: string, value: number) => {
    document.documentElement.style.setProperty(variable, `${value}px`);
  };

  useEffect(() => {
    setLoadedTrustpilot(sitecoreReady && !isMobileOrTablet);
  }, [sitecoreReady, config?.trustPilot]);

  useEffect(() => {
    dispatch(canScroll(!(showCustomise && isMobileOrTablet)));

    //this if is only required because of INT which will not have an header element
    const headerElements = document.getElementsByTagName('header');
    if (headerElements.length > 0) {
      const header = headerElements[0] as HTMLElement;
      header.style.display = showCustomise && isMobileOrTablet ? 'none' : '';
    }
  }, [showCustomise, isMobileOrTablet]);

  useEffect(() => {
    if (loadingFinalisedOptions) {
      dispatch(
        setFinalisedState({
          colours: colours,
          options: options,
        })
      );
    }

    lease.initialize();

    return () => {
      lease.dispose();
      trustPilot.dispose();
    };
  }, []);

  useEffect(() => {
    if (trustPiltoRef.current) {
      trustPilot.initialize();
    }
  }, [loadedTrustpilot && config.trustPilot]);

  useEffect(() => {
    setSidebarCSSVariable('--sidebar-lease-height', lease.size.height);
    if (trustPilot.size.height > 0) {
      setSidebarCSSVariable(
        '--sidebar-trustpilot-height',
        trustPilot.size.height
      );
    }
  }, [lease.size, trustPilot.size]);

  const maintenanceItems: string[] = [
    'Replacement tyres',
    'MOTs',
    'Servicing',
    'Brake pads',
    'Simple monthly payments',
    'Online booking',
  ];

  const initialPaymentTitle = () => {
    const usageType =
      (
        document.getElementById(
          siteWidePersonalBusinessToggleSelector
        ) as HTMLInputElement
      )?.checked === true
        ? PricingType.Business
        : PricingType.Personal;
    return usageType === PricingType.Business
      ? initialPaymentPriceExc
      : initialPaymentPriceInc;
  };

  const fireBchToggleEvent = (productType: string) => {
    window.dataLayer?.push({
      event: 'product_toggle',
      category: 'Product Type',
      action: 'toggle',
      label: productType,
    });
  };

  const selectedUsageTypeHandle = () => {
    const usageType =
      (
        document.getElementById(
          siteWidePersonalBusinessToggleSelector
        ) as HTMLInputElement
      )?.checked === true
        ? PricingType.Business
        : PricingType.Personal;
    createCookie(
      cookieName,
      usageType === PricingType.Business
        ? businessContractHire
        : personalContractHire
    );
    if (usageType === PricingType.Personal) {
      let params = new URLSearchParams(window.location.search);
      params.delete('contract_type');
      if (history.replaceState) {
        let searchString =
          params.toString().length > 0 ? '?' + params.toString() : '';
        let newUrl =
          window.location.protocol +
          '//' +
          window.location.host +
          window.location.pathname +
          searchString +
          window.location.hash;
        history.replaceState(null, '', newUrl);
      }
    }
    getVehicleInfo(
      () => updateAllPricing(usageType as PricingType),
      usageType as PricingType
    );
    dispatch(updateVehiclePricingType(usageType as PricingType));
    fireBchToggleEvent(usageType);
  };

  useEffect(() => {
    if (!cookieExists(cookieName)) {
      createCookie(
        cookieName,
        vehicleInformation.pricingType === PricingType.Business
          ? businessContractHire
          : personalContractHire
      );
    }
    document
      .getElementById(siteWidePersonalBusinessToggleSelector)
      ?.addEventListener('change', selectedUsageTypeHandle);

    return () =>
      document
        .getElementById(siteWidePersonalBusinessToggleSelector)
        ?.removeEventListener('change', selectedUsageTypeHandle);
  }, []);

  // Calculate the 1, 6 and 9 month payment button values
  const calculateInitialPayments = (
    currentPrice: number,
    months: number
  ): number => Math.round(currentPrice / 250) * 250 * months;
  return (
    <div className={`sidebar ${showCustomise ? 'sidebar--show' : ''}`}>
      <div className="sidebar__lease" ref={leaseRef}>
        {!pricingState.finished && (
          <div className="sidebar__overlay" data-cy="sidebaroverlay">
            <Loader className="sidebar__overlay--loader" />
          </div>
        )}
        <TotalCost
          setShowCustomise={setShowCustomise}
          showCustomise={showCustomise}
          pricingType={vehicleInformation.pricingType}
        />
        <div
          data-cy="sidebarCustomise"
          className={`sidebar__customise ${
            showCustomise ? 'sidebar__customise--show' : ''
          }`}
        >
          <Typography
            className="sidebar__customise-title"
            variant="h2"
            size="lg"
            weight="bold"
          >
            Customise Your Lease
          </Typography>

          {ready && (
            <>
              {pricing?.duration && (
                <QuickAction
                  minValue={pricing.duration.min}
                  midValue={36}
                  maxValue={pricing.duration.max}
                  rangeMaxValue={pricing.duration.max}
                  rangeMinValue={pricing.duration.min}
                  stepValue={pricing.duration.step}
                  testId="leaseduration"
                  variant="duration"
                  title="How long? (months)"
                  tooltipText={howLongTooltip}
                  defaultValue={searchParams.duration}
                />
              )}
              {pricing?.miles && (
                <QuickAction
                  minValue={pricing.miles.min}
                  midValue={8000}
                  maxValue={10000}
                  rangeMaxValue={pricing.miles.max}
                  rangeMinValue={pricing.miles.min}
                  stepValue={pricing.miles.step}
                  testId="leasemiles"
                  variant="miles"
                  title="How far? (miles a year)"
                  tooltipText={howFarTooltip}
                  defaultValue={searchParams.yearly_mileage}
                />
              )}
              <QuickAction
                minValue={calculateInitialPayments(initialPayment?.min, 1)}
                midValue={calculateInitialPayments(initialPayment?.min, 6)}
                maxValue={calculateInitialPayments(initialPayment?.min, 9)}
                rangeMaxValue={initialPayment?.max}
                rangeMinValue={initialPayment?.min}
                stepValue={initialPayment?.step}
                testId="leasepayment"
                variant="payment"
                title={initialPaymentTitle()}
                tooltipText={howMuchInitialPaymentTooltip}
                defaultValue={initialPayment?.default}
              />
            </>
          )}
          {pricingSelector?.maintCost != null &&
            pricingSelector?.maintCost > 0 && (
              <Maintenance data-cy="maintenance" items={maintenanceItems} />
            )}
          {!loadingFinalisedOptions && <FinalisedOptions />}
          <PricingBreakdown
            data-cy="pricingbreakdown"
            items={pricingBreakdownItems}
            pricingType={vehicleInformation.pricingType}
          />
        </div>
        <QuoteOrderBar
          showCustomise={showCustomise}
          modalSettings={modalSettings}
        />
      </div>

      {loadedTrustpilot && config.trustPilot && (
        <div className="sidebar__trustpilot" ref={trustPiltoRef}>
          <TrustPilot
            href={config.trustPilot.href}
            dataTemplateId={config.trustPilot.dataTemplateId}
            dataBusinessUnitId={config.trustPilot.dataBusinessUnitId}
          />
        </div>
      )}
    </div>
  );
};

export default Sidebar;
